import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2, Phone, Building2, Landmark, Mail,
  DollarSign, CreditCard, AlertTriangle, Loader2, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_METHODS = [
  {
    id: 'check',
    name: 'Pay by Check (In-Person Drop-off)',
    icon: CheckCircle2,
    description: 'Drop off check at our office',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'at_location',
    name: 'Pay at Location',
    icon: Building2,
    description: 'Pay in person at our office',
    color: 'from-green-500 to-green-600'
  },
  {
    id: 'phone',
    name: 'Call to Pay',
    icon: Phone,
    description: 'Pay over the phone',
    color: 'from-purple-500 to-purple-600'
  },
  {
    id: 'bank_transfer',
    name: 'Bank Transfer',
    icon: Landmark,
    description: 'Wire transfer or ACH',
    color: 'from-indigo-500 to-indigo-600'
  },
];

export default function PaymentFallbackModal({ open, onClose, cartTotal, onSuccess }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState({
    phone: '',
    message: ''
  });

  const handlePaymentMethodSelect = async (method) => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_URL}/api/cart/checkout/alternative/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_method: method.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Registration confirmed! Check your email for payment instructions.');
        onSuccess(data);
      } else {
        toast.error(data.detail || 'Failed to process registration');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to process registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContactFormSubmit = async () => {
    if (!contactForm.phone.trim()) {
      toast.error('Please provide a phone number');
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_URL}/api/cart/checkout/alternative/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_method: 'contact_request',
          contact_info: contactForm
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Request submitted! Our team will contact you within 1 business day.');
        onSuccess(data);
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Payment Issue? No Problem!
          </DialogTitle>
          <DialogDescription>
            Choose an alternative payment method below. Your spot will be reserved immediately.
          </DialogDescription>
        </DialogHeader>

        <Alert className="bg-blue-50 border-blue-200">
          <DollarSign className="h-4 w-4" />
          <AlertDescription>
            <strong>Total Amount: ${(Number(cartTotal) || 0).toFixed(2)}</strong>
            <br />
            Your enrollment will be confirmed and your spot reserved. Payment must be completed within 5 business days.
          </AlertDescription>
        </Alert>

        {!showContactForm ? (
          <>
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground">Select Payment Method:</h3>
              {PAYMENT_METHODS.map((method) => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.id}
                    onClick={() => handlePaymentMethodSelect(method)}
                    disabled={loading}
                    className="w-full p-4 rounded-lg border-2 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${method.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm group-hover:text-blue-600 transition-colors">
                          {method.name}
                        </h4>
                        <p className="text-xs text-muted-foreground">{method.description}</p>
                      </div>
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowContactForm(true)}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Still Having Issues? Contact Us
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                Our team will contact you within 1 business day to help complete your registration.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <div>
                <Label htmlFor="contact-phone">Phone Number *</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="contact-message">Additional Information (Optional)</Label>
                <Textarea
                  id="contact-message"
                  placeholder="Tell us about the issue you're experiencing or any questions you have..."
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowContactForm(false)}
                disabled={loading}
              >
                Back to Payment Options
              </Button>
              <Button
                onClick={handleContactFormSubmit}
                disabled={loading || !contactForm.phone.trim()}
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit Request
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
